import { UseRecurlyInstance } from '@recurly/react-recurly';
import { Emitter } from '@recurly/recurly-js/lib/emitter';
import {
    GooglePayEvent,
    GooglePayOptions,
    GooglePayPaymentAuthorizationResult,
} from '@recurly/recurly-js/lib/google-pay';

import { createMockGooglePayInstance } from './MockGooglePayInstance';

export type GooglePayOutcome = 'success' | 'error';

export type GooglePayInstance = Emitter<GooglePayEvent>;

export interface GooglePayServiceConfig {
    useMockGooglePay: boolean;
    mockOutcome: GooglePayOutcome;
    merchantName?: string;
    googleMerchantId?: string;
}

export interface GooglePayServiceInterface {
    getConfig(): GooglePayServiceConfig;
    isAvailable(): boolean;
    createGooglePayInstance(
        recurlyInstance?: UseRecurlyInstance,
        clientAuthorization?: string,
        currency?: string,
        total?: string,
    ): GooglePayInstance;
}

class RealGooglePayService implements GooglePayServiceInterface {
    private config: GooglePayServiceConfig;

    constructor(config: GooglePayServiceConfig) {
        this.config = config;
    }

    getConfig(): GooglePayServiceConfig {
        return this.config;
    }

    isAvailable(): boolean {
        // Google Pay is available if:
        // 1. We're using mock mode, OR
        // 2. We have a Google Merchant ID configured
        return this.config.useMockGooglePay || !!this.config.googleMerchantId;
    }

    createGooglePayInstance(
        recurlyInstance?: UseRecurlyInstance,
        clientAuthorization?: string,
        currency: string = 'USD',
        total: string = '0',
    ): GooglePayInstance {
        if (this.config.useMockGooglePay) {
            return createMockGooglePayInstance(this.config.mockOutcome);
        }

        if (!recurlyInstance) {
            throw new Error('Recurly instance is required for real Google Pay integration');
        }

        // Check if Google Merchant ID is required and missing
        if (!this.config.googleMerchantId && !clientAuthorization) {
            throw new Error(
                'Google Merchant ID is required for Google Pay integration. Please set NEXT_PUBLIC_GOOGLE_MERCHANT_ID environment variable.',
            );
        }

        // Determine Google Pay environment based on NODE_ENV
        // PRODUCTION: Live cards will be charged
        // TEST: Shows "won't be charged" message and accepts test cards
        const isProduction = process.env.NODE_ENV === 'production';
        const googlePayEnvironment = isProduction ? 'PRODUCTION' : 'TEST';

        const googlePayConfig: GooglePayOptions = {
            currency,
            country: 'US',
            total,
            billingAddressRequired: true,
            // @ts-expect-error allowing environment key as part of eslint migration
            environment: googlePayEnvironment,
            googleMerchantId: this.config.googleMerchantId || '',
            merchantName: this.config.merchantName || 'Storyblocks', // Always include merchant name
            // Button customization options - nested under buttonOptions per Recurly API spec
            buttonOptions: {
                buttonType: 'pay', // Shows "Pay with G Pay" text
                buttonColor: 'default', // Default Google Pay styling (black on light backgrounds)
                buttonSizeMode: 'fill', // Fill the container width
                buttonLocale: 'en', // English locale
            },
            paymentOptions: {
                requestPayerName: true,
                requestPayerEmail: true,
                requestPayerPhone: false,
                requestShippingAddress: false,
                allowPrepaidCards: true,
                allowCreditCards: true,
            },
            callbacks: {
                onPaymentAuthorized: (): Promise<GooglePayPaymentAuthorizationResult> | void => {
                    // This callback is handled by the component
                    return;
                },
            },
        };

        // Add Braintree configuration if client authorization is provided
        if (clientAuthorization) {
            googlePayConfig.braintree = { clientAuthorization };
        }

        return recurlyInstance.GooglePay(googlePayConfig);
    }
}

function createGooglePayService(): GooglePayServiceInterface {
    const useMockGooglePay = process.env.NEXT_PUBLIC_MOCK_GOOGLE_PAY === 'true';
    const rawOutcome = process.env.NEXT_PUBLIC_MOCK_GOOGLE_PAY_OUTCOME;
    const mockOutcome = (['success', 'error'] as Array<string | undefined>).includes(rawOutcome)
        ? (rawOutcome as GooglePayOutcome)
        : 'success';
    const merchantName = process.env.NEXT_PUBLIC_GOOGLE_MERCHANT_NAME || 'Storyblocks';
    const googleMerchantId = process.env.NEXT_PUBLIC_GOOGLE_MERCHANT_ID || '';

    const config: GooglePayServiceConfig = {
        useMockGooglePay,
        mockOutcome,
        merchantName,
        googleMerchantId,
    };

    return new RealGooglePayService(config);
}

export const GooglePayService = createGooglePayService();
